import debounce from 'lodash.debounce';

import loggerService from './logger.service';

import type { MessageReaction } from '@/store/modules/avroom/types';

import { getEnv } from '@/constants';
import i18n from '@/i18n';
import store from '@/store';
import { User } from '@/store/modules/event/types';
import { getFormattedTextQNA } from '@/store/modules/textQNA/actions';
import {
  emojisReaction,
  filterMode,
  sortMode,
  TextQADetail,
  TextQNAPost,
  TextQNAQuestionDetails
} from '@/store/modules/textQNA/types';

/************************************************/
// Core Data vars
/***********************************************/

//This is holding every post of Text QnA channel which is active right now
let questionsMap: Record<string, TextQNAQuestionDetails> = {};

/*************************************************/
// TYPES
/*************************************************/

type FormattedTextQNAQuestions = TextQNAQuestionDetails[];
type AnswerIDMap = Record<string, TextQNAPost>;

/************************************************/
// API CALLS
/***********************************************/

/**
 *
 * @param payload
 * @returns
 */
export const createAPostInChannel = (payload: {
  channelId: string;
  message: string;
  askAnonymously?: boolean;
  props?: any;
  rootId?: string;
  retrycount?: number;
}) => {
  return store.dispatch('event/avroom/createAPostInChannel', {
    ...payload,
    channel_id: payload.channelId,
    root_id: payload.rootId
  });
};

/**
 *
 * @param payload
 * @returns
 */
export const updateAPostInChannel = (payload: {
  channelId: string;
  message: string;
  postId: string;
  props?: any;
  rootId: string;
  retrycount?: number;
}) => {
  const requestParams: any = {
    channel_id: payload.channelId,
    message: payload.message,
    post_id: payload.postId,
    root_id: payload.rootId,
    props: payload?.props ?? {}
  };

  const currentEventUser = store.state.user.currentEventUser;
  if (currentEventUser) {
    let name = currentEventUser.name;
    if (!name || name.trim().length === 0) {
      name = currentEventUser.email?.split('@')[0];
    }
    requestParams.identifiers = {
      name: name,
      mediumimage: currentEventUser.mediumimage,
      thumbnail: currentEventUser?.thumbnail,
      is_in_person: currentEventUser.eventUser?.is_in_person ?? false,
      coreuserid: currentEventUser.id
    };
  }

  return store.dispatch(
    'mattermost/makeMattermostCall',
    {
      url: `/api/v4/posts/${payload.postId}/patch`,
      method: 'PUT',
      body: requestParams
    },
    { root: true }
  );
};

export const assignQuestionToSpeaker = (payload: { channelId: string; rootId: string; assignedTo: string }) => {
  const requestParams: any = {
    assignedTo: payload.assignedTo || ''
  };

  return store.dispatch(
    'mattermost/makeMattermostCall',
    {
      url: `/api/v4/posts/${payload.rootId}/props`,
      method: 'PUT',
      body: requestParams
    },
    { root: true }
  );
};

/**
 *
 * @param param
 * @returns
 */
export const fetchChannelPosts = ({
  channelId,
  noOfMessagesToLoad,
  referencePostID,
  referenceDirection
}: {
  channelId: string;
  noOfMessagesToLoad?: number;
  referencePostID?: string;
  referenceDirection?: 'previous' | 'next';
}) => {
  return store.dispatch('event/avroom/fetchChannelPosts', {
    channel_id: channelId,
    noOfMessagesToLoad,
    referencePostID,
    referenceDirection
  });
};

export const postAReaction = (payload: {
  postId: string;
  userId: string;
  emojiName: string;
  forcedSyncFlow?: Boolean;
}) => {
  const useExternal =
    store.state.event?.event?.scalability_flags?.use_external_apis && !store.getters['event/isOrganizerOrSpeaker'];

  if (useExternal && !payload.forcedSyncFlow) {
    return store.dispatch(
      'mattermost/makeMattermostCall',
      {
        url: `${getEnv('VUE_APP_MM_SLOW_API')}`,
        method: 'PUT',
        body: {
          type: 'reaction_add',
          payload: {
            post_id: payload.postId,
            user_id: payload.userId,
            emoji_name: payload.emojiName
          }
        },
        configs: {
          useGivenUrl: true,
          addTokenToBodyItself: true
        }
      },
      {
        root: true
      }
    );
  }
  return store.dispatch(
    'mattermost/makeMattermostCall',
    {
      url: `/api/v4/reactions`,
      method: 'POST',
      body: {
        post_id: payload.postId,
        user_id: payload.userId,
        emoji_name: payload.emojiName,
        create_at: Date.now()
      }
    },
    {
      root: true
    }
  );
};

/**
 *
 * @param payload
 * @returns
 */
export const removeAReaction = (payload: { postId: string; userId: string; emojiName: string }): Promise<void> => {
  const useExternal =
    store.state.event?.event?.scalability_flags?.use_external_apis && !store.getters['event/isOrganizerOrSpeaker'];

  if (useExternal) {
    return store.dispatch(
      'mattermost/makeMattermostCall',
      {
        url: `${getEnv('VUE_APP_MM_SLOW_API')}`,
        method: 'PUT',
        body: {
          type: 'reaction_delete',
          payload: {
            post_id: payload.postId,
            user_id: payload.userId,
            emoji_name: payload.emojiName
          }
        },
        configs: {
          useGivenUrl: true,
          addTokenToBodyItself: true
        }
      },
      {
        root: true
      }
    );
  }
  return store.dispatch(
    'mattermost/makeMattermostCall',
    {
      url: `/api/v4/reactions/delete`,
      method: 'POST',
      body: JSON.stringify({
        post_id: payload.postId,
        user_id: payload.userId,
        emoji_name: payload.emojiName
      })
    },
    {
      root: true
    }
  );
};

/********************************************/
// updation of text Qna posts and debouncing
/*******************************************/

export const getTextQNAQuestionMap = () => {
  return questionsMap;
};

export const resetTextQNAQuestionsMap = () => {
  questionsMap = {};
};

export const migratedPostRecieved = (msg: any) => {
  try {
    const post = JSON.parse(msg.data.post);
    if (store.state.event.textQNA?.textQnA?.id === post?.channel_id) {
      addTextQnAPost(post);
    }
  } catch (error) {
    loggerService.error('Error occurred while migrated post recieve: ', error);
  }
};

export const newPostReceived = (post: any) => {
  addTextQnAPost(post);
};

export const textQnAReactionRecieved = (reaction: MessageReaction) => {
  textQNAReactionReceived(reaction, 'add');
  debounceUpdateTextQADetails();
};

export const textQnAReactionRemoved = (reaction: MessageReaction) => {
  textQNAReactionReceived(reaction, 'remove');
  debounceUpdateTextQADetails();
};

export const textQnAPostDelete = (post: any) => {
  removeTextQNAPost(getFormattedTextQNA(post));
  debounceUpdateTextQADetails();
};

/********************************************/
//  UTIL FUNCTIONS
/*******************************************/

/* c8 ignore next */
const getAnswerPostIDMap = (textQNAPosts: TextQNAPost[]): AnswerIDMap => {
  const answerPostIDMap: AnswerIDMap = {};
  for (let i = 0; i < textQNAPosts.length; i++) {
    const post = textQNAPosts[i];
    if (post.root_id) {
      answerPostIDMap[post.root_id] = post;
    }
  }
  return answerPostIDMap;
};

export const setTextQNAPosts = (textQNAPosts: TextQNAPost[]) => {
  try {
    const textQnA = store.state.event.textQNA?.textQnA;
    let publishedPostId = textQnA?.detail?.publishedQuestion?.id ?? '';
    const currentUser = (store.state as any).user.currentUser;
    const answerMap = getAnswerPostIDMap(textQNAPosts);

    textQNAPosts.forEach((question: TextQNAPost) => {
      const isPublishedPost = question?.props?.publishedPost;
      if (!question.root_id && !isPublishedPost) {
        questionsMap[question.id] = formattedTextQNAQuestion(
          question,
          textQnA?.isModerated || false,
          currentUser,
          answerMap[question.id] || {}
        );
      } else if (isPublishedPost) {
        publishedPostId = isPublishedPost === 'NO_POST_PUBLISHED' ? '' : isPublishedPost;
      }
    });
    setPublishedPost(publishedPostId);
    debounceUpdateTextQADetails();
  } catch (error) {
    /* c8 ignore next */
    loggerService.error("Error while settings text qna's ", error);
  }
};

export const resetModeratePosts = (isModerated: boolean) => {
  Object.keys(questionsMap).forEach(questionId => {
    const question = questionsMap[questionId];

    if (question && !isModerated) {
      question.approvedByMods = true;
    } else if (question) {
      question.approvedByMods = question.totalOkHand > 0;
    }
    questionsMap[questionId] = question;
  });
};

const addTextQnAPost = (post: TextQNAPost) => {
  const formattedPost = getFormattedTextQNA(post);

  if (formattedPost.root_id) {
    const question = questionsMap[formattedPost.root_id];
    question.answer = formattedPost.text;
    question.answered = !!formattedPost.text;
    question.answerId = formattedPost.id;
    question.answeredByUser = {
      id: formattedPost?.identifiers?.coreuserid ?? '',
      name: formattedPost?.identifiers?.name ?? 'name not found',
      mediumimage: formattedPost?.identifiers.mediumimage ?? '',
      thumbnail: formattedPost?.identifiers?.thumbnail ?? '',
      is_in_person: formattedPost?.identifiers?.is_in_person ?? false
    };

    questionsMap[formattedPost.root_id] = question;
    debounceUpdateTextQADetails();
    return;
  }
  setTextQNAPosts([formattedPost]);
};

const removeTextQNAPost = (payload: TextQNAPost) => {
  if (payload.root_id && payload.id) {
    const question = questionsMap[payload.root_id];
    question.answer = '';
    question.answered = false;
    question.answerId = '';
  } else if (payload.id && !payload.root_id) {
    delete questionsMap[payload.id];
  }
};

const textQNAReactionReceived = (reaction: MessageReaction, actionType: 'add' | 'remove') => {
  const question = questionsMap[reaction.post_id];
  const currentUser = (store.state as any).user.currentUser;

  if (question) {
    if (reaction.emoji_name === emojisReaction.APPROVED) {
      question.totalOkHand = actionType === 'add' ? question.totalOkHand + 1 : question.totalOkHand - 1;

      question.approvedByMods = question.totalOkHand > 0;
    } else if (reaction.emoji_name === emojisReaction.ANSWERED) {
      question.answered = actionType === 'add';
    } else if (reaction.emoji_name === emojisReaction.PRIORITIZED) {
      question.isPrioritized = actionType === 'add';
    } else if (reaction.emoji_name === emojisReaction.UPVOTED) {
      if (reaction.user_id === currentUser?.mmid) {
        if (!question.upvotedByUser) {
          question.totalUpvotes = question.totalUpvotes + 1;
          question.upvotedByUser = true;
        }
      } else {
        question.totalUpvotes = actionType === 'add' ? question.totalUpvotes + 1 : question.totalUpvotes - 1;
      }
    }
  }
};

/* c8 ignore next */
const setPublishedPost = (publishedPostId: string) => {
  if (publishedPostId === null) {
    return;
  }

  if (publishedPostId && questionsMap[publishedPostId]) {
    store.commit('event/textQNA/setPublishedPost', questionsMap[publishedPostId], {
      root: true
    });
    return;
  }

  if (publishedPostId?.length !== 0) {
    loggerService.warn('Post is published but unable to find post details: ', publishedPostId);
  }

  store.commit('event/textQNA/setPublishedPost', null, {
    root: true
  });
};

/**********************************************/
// Update text qna details
/**********************************************/

const debouncedActions: Record<string, (channel_id?: string) => void> = {};

function makeDebouncedTextQAUpdates(debounceTime: number): () => void {
  const text_qna_debounced_key = 'text_qna_updates_channel';
  if (debouncedActions[text_qna_debounced_key]) {
    return debouncedActions[text_qna_debounced_key];
  }
  debouncedActions[text_qna_debounced_key] = debounce(
    () => {
      updateTextQADetails();
    },
    debounceTime,
    {
      maxWait: debounceTime,
      leading: false,
      trailing: true
    }
  );
  return debouncedActions[text_qna_debounced_key];
}

/* c8 ignore next */
export const debounceUpdateTextQADetails = () => {
  const debouncedTimeForTextQAUIUpdates = 1000;

  if (debouncedTimeForTextQAUIUpdates <= 0) {
    updateTextQADetails();
    return;
  }
  const debouncedUpdateTextQA = makeDebouncedTextQAUpdates(debouncedTimeForTextQAUIUpdates);
  debouncedUpdateTextQA();
};
export const updateTextQADetails = () => {
  filterAndSortQna();
};

/**
 *
 * @param question
 * @param isModerated
 * @param currentUser
 * @param answer
 * @returns
 */
/* c8 ignore next */
function formattedTextQNAQuestion(
  question: TextQNAPost,
  isModerated: boolean,
  currentUser: User,
  answer: any
): TextQNAQuestionDetails {
  const timeformatter = Intl.DateTimeFormat('en-US', {
    hour12: true,
    hour: 'numeric',
    minute: 'numeric'
  });
  let isAnswered = false;
  let isApprovedByMods = !isModerated;
  let totalOkHand = 0;
  let votedByUser = false;
  let totalUpvotes = 0;
  let isPrioritized = false;

  const reactions = question && question.reactions ? question.reactions : [];
  const answerText = answer?.text || '';
  const answerId = answer?.id || '';

  reactions.forEach(reaction => {
    if (reaction.emoji_name === emojisReaction.APPROVED) {
      isApprovedByMods = true;
      totalOkHand = totalOkHand + 1;
    } else if (reaction.emoji_name === emojisReaction.ANSWERED) {
      isAnswered = true;
    } else if (reaction.emoji_name === emojisReaction.PRIORITIZED) {
      isPrioritized = true;
    } else if (reaction.emoji_name === emojisReaction.UPVOTED) {
      totalUpvotes = totalUpvotes + 1;
      if (reaction.user_id === currentUser?.mmid) {
        votedByUser = true;
      }
    }
  });
  const questionCreationTime = question.createdAt || Date.now();

  return {
    questionText: question?.text || '',
    id: question?.id || '',
    upvotedByUser: votedByUser,
    totalOkHand: totalOkHand,
    totalUpvotes: totalUpvotes,
    time: timeformatter.format(questionCreationTime),
    approvedByMods: isApprovedByMods,
    answered: isAnswered || !!answerText,
    created_at: questionCreationTime,
    isPrioritized: isPrioritized,
    user: {
      id: question?.identifiers?.coreuserid ?? '',
      mediumimage: question?.identifiers?.mediumimage ?? '',
      thumbnail: question?.identifiers?.thumbnail ?? '',
      name: question?.identifiers?.name ?? 'name not found',
      is_in_person: question?.identifiers?.is_in_person ?? false
    },
    answer: answerText,
    answerId: answerId,
    answeredByUser: {
      id: answer?.identifiers?.coreuserid ?? '',
      name: answer?.identifiers?.name ?? 'name not found',
      thumbnail: answer?.identifiers?.thumbnail ?? '',
      mediumimage: answer?.identifiers?.mediumimage ?? '',
      is_in_person: answer?.identifiers?.is_in_person ?? false
    },
    props: question.props
  };
}

function filterAndSortQna() {
  const filterTextQnA = new FilterTextQnA();
  const sortQna = new SortQnA();

  const textQNADetail: Partial<TextQADetail> = {
    questions: [],
    numberOfUserUnModdedQues: 0,
    totalNoOfQuestions: 0
  };

  const isTextQNAUpvotingDisabled = false;
  const isUpvotingDisabledOnTextQNAs = false;

  const isSortingDisabled = isTextQNAUpvotingDisabled || isUpvotingDisabledOnTextQNAs;
  const isFilteringDisabled = false;

  const filterModeValue = store.state.event.textQNA?.textQnA?.filtermode.value || filterMode.ALL;
  const sortModeValue = store.state.event.textQNA?.textQnA?.sortmode.value || sortMode.UPVOTES;
  const speakerFilterValue = store.state.event.textQNA?.textQnA?.speakerFilter.value || 'all';

  const publishedPostId = store.state.event.textQNA?.textQnA?.detail.publishedQuestion?.id ?? '';
  const isSpeakerOrOrganizer = store.getters['event/isBroadcastSpeakerOrOrganizer'];
  const currentUser = (store.state as any).user.currentUser;
  const currentUserMmid = currentUser?.mmid;

  if (publishedPostId) {
    setPublishedPost(publishedPostId);
  }

  //filtering and sorting here

  let filteredAndSortQuestions = Object.values(questionsMap).filter(question => {
    const isQuestionDeleted = question?.props?.isDeleted ?? false;
    const isQuestionAssignedToSpeaker = question?.props?.assignedTo === speakerFilterValue;
    const isAssignSpeakerQnAEnabled = store.getters['event/currentEvent'].assign_speaker_qna;

    return (
      (question?.approvedByMods ||
        question?.user?.id === currentUser?.id ||
        question?.user?.id === currentUserMmid ||
        isSpeakerOrOrganizer) &&
      isQuestionDeleted === false &&
      (!isAssignSpeakerQnAEnabled || speakerFilterValue === 'all' || isQuestionAssignedToSpeaker)
    );
  });

  const copyOfListOfQuestionDetails = filteredAndSortQuestions;

  if (!isFilteringDisabled) {
    filteredAndSortQuestions = filterTextQnA.filterTextQnaQuestions(filterModeValue, filteredAndSortQuestions);
  }

  if (!isSortingDisabled) {
    filteredAndSortQuestions = sortQna.sortTextQnaQuestions(sortModeValue, filteredAndSortQuestions);
  }

  textQNADetail.questions = filteredAndSortQuestions;
  textQNADetail.numberOfUserUnModdedQues =
    filterTextQnA.getCurrentUserUnApprovedQuestions(copyOfListOfQuestionDetails)?.length || 0;
  textQNADetail.totalNoOfQuestions = Object.keys(questionsMap).length;
  store.commit('event/textQNA/updateTextQnADetails', textQNADetail, {
    root: true
  });
}

class FilterTextQnA {
  /* c8 ignore next */
  filterTextQnaQuestions(
    filteringMode: string,
    listOfQuestionDetails: FormattedTextQNAQuestions
  ): FormattedTextQNAQuestions {
    if (filteringMode === filterMode.APPROVED) {
      return this.getApprovedQuestions(listOfQuestionDetails);
    }

    if (filteringMode === filterMode.UNAPPROVED) {
      return this.getUnApprovedQuestions(listOfQuestionDetails);
    }

    if (filteringMode === filterMode.ANSWERED) {
      return this.getAnsweredQuestions(listOfQuestionDetails);
    }

    if (filteringMode === filterMode.UNANSWERED) {
      return this.getUnAnsweredQuestions(listOfQuestionDetails);
    }

    if (filteringMode === filterMode.PRIORITIZED) {
      return this.getPrioritizedQuestions(listOfQuestionDetails);
    }

    if (filteringMode === filterMode.MYQUESTIONS) {
      return this.getMyQuestions(listOfQuestionDetails);
    }

    if (filteringMode === filterMode.UNANSWEREDANDUNAPPROVED) {
      return this.getUnApprovedAndUnAnsweredQuestions(listOfQuestionDetails);
    }

    if (filteringMode === filterMode.UNASSIGNED) {
      return this.getUnAssignedQuestions(listOfQuestionDetails);
    }

    return listOfQuestionDetails;
  }

  getAnsweredQuestions = (listOfQuestionDetails: FormattedTextQNAQuestions) => {
    try {
      return listOfQuestionDetails.filter((question: TextQNAQuestionDetails) => question.answered === true);
    } catch (error) {
      loggerService.error('Error occured while filtering getAnsweredQuestions: ', error);
      return listOfQuestionDetails;
    }
  };

  getUnAnsweredQuestions = (listOfQuestionDetails: FormattedTextQNAQuestions) => {
    try {
      return listOfQuestionDetails.filter((question: TextQNAQuestionDetails) => question.answered === false);
    } catch (error) {
      loggerService.warn('Error occured while filtering getUnAnsweredQuestions: ', error);
      return listOfQuestionDetails;
    }
  };

  getApprovedQuestions = (listOfQuestionDetails: FormattedTextQNAQuestions) => {
    try {
      return listOfQuestionDetails.filter((question: TextQNAQuestionDetails) => question.approvedByMods === true);
    } catch (error) {
      loggerService.warn('Error occured while filtering getApprovedQuestions: ', error);
      return listOfQuestionDetails;
    }
  };

  getCurrentUserUnApprovedQuestions = (listOfQuestionDetails: FormattedTextQNAQuestions) => {
    try {
      const currentUser = (store.state as any).user.currentUser;
      const currentUserMmid = currentUser?.mmid;
      return listOfQuestionDetails.filter(
        (question: TextQNAQuestionDetails) =>
          question.approvedByMods === false &&
          (question.user.id === currentUser?.id || question.user.id === currentUserMmid)
      );
    } catch (error) {
      loggerService.warn('Error occured while filtering getUnApprovedQuestions: ', error);
      return listOfQuestionDetails;
    }
  };

  getUnApprovedQuestions = (listOfQuestionDetails: FormattedTextQNAQuestions) => {
    try {
      return listOfQuestionDetails.filter((question: TextQNAQuestionDetails) => question.approvedByMods === false);
    } catch (error) {
      loggerService.warn('Error occured while filtering getUnApprovedQuestions: ', error);
      return listOfQuestionDetails;
    }
  };

  getUnApprovedAndUnAnsweredQuestions = (listOfQuestionDetails: FormattedTextQNAQuestions) => {
    try {
      return listOfQuestionDetails.filter(
        (question: TextQNAQuestionDetails) => question.approvedByMods === false || question.answered === false
      );
    } catch (error) {
      loggerService.warn('Error occured while filtering getUnApprovedAndUnAnsweredQuestions: ', error);
      return listOfQuestionDetails;
    }
  };

  getUnAssignedQuestions = (listOfQuestionDetails: FormattedTextQNAQuestions) => {
    try {
      return listOfQuestionDetails.filter((question: TextQNAQuestionDetails) => !question.props?.assignedTo);
    } catch (error) {
      loggerService.warn('Error occured while filtering getUnAssignedQuestions: ', error);
      return listOfQuestionDetails;
    }
  };

  getMyQuestions = (listOfQuestionDetails: FormattedTextQNAQuestions) => {
    try {
      const currentUser = (store.state as any).user.currentUser;
      const currentUserMmid = currentUser?.mmid;
      return listOfQuestionDetails.filter(
        (question: TextQNAQuestionDetails) =>
          question.user.id === currentUser?.id || question.user.id === currentUserMmid
      );
    } catch (error) {
      loggerService.warn('Error occured while filtering getMyQuestions: ', error);
      return listOfQuestionDetails;
    }
  };
  getPrioritizedQuestions = (listOfQuestionDetails: FormattedTextQNAQuestions) => {
    try {
      return listOfQuestionDetails.filter((question: TextQNAQuestionDetails) => question.isPrioritized);
    } catch (error) {
      loggerService.warn('Error occured while filtering getPrioritizedQuestions: ', error);
      return listOfQuestionDetails;
    }
  };
}

class SortQnA {
  /* c8 ignore next */
  sortTextQnaQuestions = (sortingMode: string, listOfQuestionDetails: FormattedTextQNAQuestions) => {
    if (sortingMode === sortMode.UPVOTES) {
      return listOfQuestionDetails.sort(this.sortByUpvotes);
    }
    return listOfQuestionDetails.sort(this.sortByRecency);
  };

  sortByUpvotes = (a: TextQNAQuestionDetails, b: TextQNAQuestionDetails) => {
    try {
      return this.getSortValuesByUpvotes(a, b);
    } catch (err) {
      loggerService.error('error in sortByUpvotes', err);
      return 1;
    }
  };

  sortByRecency = (a: TextQNAQuestionDetails, b: TextQNAQuestionDetails) => {
    try {
      return this.getRecentSortedFirst(a, b);
    } catch (err) {
      loggerService.error('error in sortByRecency', err);
      return 1;
    }
  };

  getRecentSortedFirst = (a: TextQNAQuestionDetails, b: TextQNAQuestionDetails) => {
    if (a.created_at < b.created_at) {
      return 1;
    }
    if (a.created_at > b.created_at) {
      return -1;
    }
    return 0;
  };

  getRecentSortedLast(a: TextQNAQuestionDetails, b: TextQNAQuestionDetails) {
    if (a.created_at > b.created_at) {
      return 1;
    }
    if (a.created_at < b.created_at) {
      return -1;
    }
    return 0;
  }

  getSortValuesByUpvotes = (a: TextQNAQuestionDetails, b: TextQNAQuestionDetails) => {
    if (a.totalUpvotes === b.totalUpvotes) {
      return this.getRecentSortedFirst(a, b);
    }
    if (a.totalUpvotes > b.totalUpvotes) {
      return -1;
    }
    if (a.totalUpvotes < b.totalUpvotes) {
      return 1;
    }
    return 0;
  };
}

/**********************************/
// Empty State Text QNA Meta Data
/**********************************/
/* c8 ignore next */
export const TextQnAEmptyStateConfig = (key: string) => {
  const data = {
    speaker_myquestions: {
      title: i18n.global.t("@@translate@@ You haven't asked any questions yet"),
      message: ''
    },
    attendee_myquestions: {
      title: i18n.global.t("@@translate@@ You haven't asked any questions yet"),
      message: ''
    },
    speaker_prioritized: {
      title: i18n.global.t('@@translate@@ No prioritized questions'),
      message: i18n.global.t(
        '@@translate@@ Create prioritized list of questions for speakers by marking questions as priority'
      )
    },
    speaker_approved: {
      title: i18n.global.t('@@translate@@ No Approved Questions'),
      message: i18n.global.t('@@translate@@ Approve incoming questions to see list of approved questions')
    },
    speaker_unapproved: {
      title: i18n.global.t('@@translate@@ No Unapproved Questions'),
      message: i18n.global.t('@@translate@@ Tell your audience to send in questions!')
    },
    speaker_answered: {
      title: i18n.global.t('@@translate@@ No Answered Questions'),
      message: i18n.global.t('@@translate@@ Answer a question on stage or with inline text')
    },
    speaker_unanswered: {
      title: i18n.global.t('@@translate@@ All questions are answered'),
      message: i18n.global.t('@@translate@@ Tell your audience to send in questions!')
    },
    speaker_all: {
      title: i18n.global.t('@@translate@@ No Questions Yet'),
      message: i18n.global.t('@@translate@@ Tell your audience to send in questions!')
    },
    attendee_all: {
      title: i18n.global.t('@@translate@@ No Questions Yet'),
      message: i18n.global.t('@@translate@@ Be the first to ask a question!')
    },
    speaker_unassigned: {
      title: i18n.global.t('@@translate@@ No Unassigned Questions'),
      message: i18n.global.t('@@translate@@ All questions are assigned to speakers')
    },
    speaker_assigned: {
      title: i18n.global.t('@@translate@@ No Assigned Questions'),
      message: i18n.global.t('@@translate@@ Assign questions to this speaker')
    },
    speaker_assigned_to_you: {
      title: i18n.global.t('@@translate@@ No questions for you yet'),
      message: i18n.global.t('@@translate@@ Questions will appear here when a Staff member assigns them to you')
    }
  };
  return data[key];
};
